import { useCallback } from 'react'

import { ButtonGroup, useAsyncResource, useNavigation } from '@subaio/components'
import { logError } from '@subaio/utils'

import { billingAuth } from 'lib/billingAuth'
import { asContainer } from 'lib/container'
import { assertAuthenticated, isB2C } from 'lib/userAuth'

import { ButtonSolid } from 'components/Buttons'
import { CenterContent } from 'components/Layout'
import { LoadingScreen } from 'components/LoadingScreen'
import { SnackbarType, useSnackbar } from 'components/Snackbars'

export const container = asContainer<{}>()({
	path: '/billing',
	authentication: { type: 'private' },
	Running: ({ api, query, userAuth }) => {
		assertAuthenticated(userAuth)

		const { onUpdateUrlTo } = useNavigation()
		const { showSnackbar } = useSnackbar()
		const isBillingReturn = query?.['billing-confirmed'] === 'true'

		const loadBilling = useAsyncResource<'pending' | 'redirecting'>(
			useCallback(async () => {
				try {
					const { billingStatus } = await api.get<{ billingStatus: string; billingUrl: string }>('/billing/status')

					if (billingStatus === 'ACTIVE') {
						billingAuth.clear()
						if (!isB2C(userAuth.user.token))
							showSnackbar({ message: 'Payment confirmed. You are now a paid user.', type: SnackbarType.Success })
						onUpdateUrlTo({ name: 'Home' })
						return 'redirecting'
					}

					if (isBillingReturn) {
						return 'pending'
					}

					const { billingPaymentUrl } = await api.get<{ billingPaymentUrl: string }>('/billing/payment-url')
					billingAuth.saveDesign()
					document.location.replace(billingPaymentUrl)
					return 'redirecting'
				} catch (error) {
					logError(error)
					showSnackbar({ message: 'Failed to load payment information', type: SnackbarType.Error })
					throw error
				}
			}, [api, isBillingReturn, onUpdateUrlTo, showSnackbar, userAuth.user]),
		)

		if (loadBilling.status === 'loading') return <LoadingScreen />

		if (loadBilling.status === 'errored') {
			return (
				<CenterContent>
					<h1>Add payment information</h1>
					<p>We could not open the payment page. Please try again.</p>
					<ButtonGroup center>
						<ButtonSolid label='Try again' onClick={loadBilling.refresh} />
					</ButtonGroup>
				</CenterContent>
			)
		}

		if (loadBilling.value === 'pending') {
			return (
				<CenterContent>
					<h1>Payment confirmation pending</h1>
					<p>Your payment has not been confirmed yet. Please try again in a moment.</p>
					<ButtonGroup center>
						<ButtonSolid label='Try again' onClick={loadBilling.refresh} />
					</ButtonGroup>
				</CenterContent>
			)
		}

		return <LoadingScreen />
	},
})
